import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type AuthStatus } from './lib/api.js';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { MatchingPage } from './pages/MatchingPage.js';
import { CostsPage } from './pages/CostsPage.js';
import { DailyPage } from './pages/daily/DailyPage.js';
import { Layout } from './components/Layout.js';
import {
  legacyPane,
  paneFromPath,
  pathForPane,
  SettingsPage,
} from './pages/settings/SettingsPage.js';

/**
 * Auth gate + a tiny history-API router. Unauthenticated visitors see the login
 * screen wherever they land (the original served login.html for / and /matching
 * alike); signed-in users get the dashboard, the matching page, or settings.
 */
export function App() {
  const status = useQuery({
    queryKey: ['auth-status'],
    queryFn: () => api.get<AuthStatus>('/api/auth/status'),
  });

  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const sync = () => setPath(window.location.pathname);
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  const navigate = useCallback((to: string) => {
    window.history.pushState(null, '', to);
    // The route is the pathname alone: a target may carry a hash (the Stats
    // page's drill-down deep-links to /matching#feed-<id>) that the page it
    // lands on reads for itself.
    const url = new URL(to, window.location.origin);
    setPath(url.pathname);
    window.scrollTo(0, 0);
    // pushState fires no hashchange: a page already showing (Email & Sheets,
    // when its header item is clicked from itself) still gets to jump.
    if (url.hash) window.dispatchEvent(new HashChangeEvent('hashchange'));
  }, []);

  // Configuration was /settings before it was renamed, and #settings/<pane>
  // before that. Those addresses still work: they're rewritten to the current
  // path with replaceState, so Back doesn't bounce off the old one.
  useEffect(() => {
    const rewriteLegacy = () => {
      const pane = legacyPane(window.location.pathname, window.location.hash);
      if (!pane) return;
      window.history.replaceState(null, '', pathForPane(pane));
      setPath(window.location.pathname);
    };
    rewriteLegacy();
    window.addEventListener('hashchange', rewriteLegacy);
    return () => window.removeEventListener('hashchange', rewriteLegacy);
  }, []);

  if (status.isLoading) {
    return <div className="centered">Loading…</div>;
  }
  if (status.isError) {
    return (
      <div className="centered">
        <div style={{ textAlign: 'center' }}>
          <p>Cannot reach the server.</p>
          <button onClick={() => void status.refetch()}>Retry</button>
        </div>
      </div>
    );
  }

  const data = status.data!;
  if (!data.user) {
    return <LoginPage needsSetup={data.needsSetup} />;
  }

  const pane = paneFromPath(path);
  return (
    <Layout path={path} navigate={navigate} username={data.user}>
      {pane ? (
        <SettingsPage pane={pane} navigate={navigate} />
      ) : path === '/matching' ? (
        <MatchingPage />
      ) : path === '/flagged' ? (
        <MatchingPage view="flagged" />
      ) : path === '/costs' ? (
        <CostsPage />
      ) : path === '/daily' ? (
        <DailyPage />
      ) : (
        <DashboardPage navigate={navigate} />
      )}
    </Layout>
  );
}
